import { SemanticCOLORS } from 'semantic-ui-react/dist/commonjs/generic';

type DialogAction<T> = (data?: T, others?: any) => Promise<any>;

export interface DialogMessage {
    title: string;
    message: string;
    inputType?: string;
    type: 'alert' | 'confirm' | 'text';
    actions: {
        text: string;
        icon?: string;
        color?: SemanticCOLORS;
        action?: DialogAction<any>;
        data?: any;
        loading?: boolean;
    }[];
    id?: number;
    open?: boolean;
    inputValue?: string;
}

class DialogControl {
    static currentId = 0;
    receiver?: (message: DialogMessage) => void;
    queue: DialogMessage[] = [];

    private show(message: DialogMessage) {
        message.id = ++DialogControl.currentId;
        message.open = true;
        if (this.receiver) {
            this.receiver(message);
        } else {
            this.queue.push(message);
        }

        if (DialogControl.currentId >= Number.MAX_VALUE) {
            DialogControl.currentId = 0;
        }
    }

    public alert(message: string, action?: DialogAction<any>, parameters?: { [key: string]: string } | string, tag?: string, plural?: boolean) {
        this.show({
            title: 'warning',
            message,
            type: 'alert',
            actions: [{ text: 'Confirm', action }]
        });
    }

    public alertText(title: string, message: string, action?: DialogAction<any>) {
        this.show({
            title,
            message,
            type: 'text',
            actions: [{ text: 'Close', action }]
        });
    }

    public confirm(
        message: string,
        action?: (agree: boolean) => Promise<any>,
        parameters?: { [key: string]: string } | string,
        tag?: string,
        plural?: boolean
    ) {
        this.show({
            title: 'warning',
            message,
            type: 'confirm',
            actions: [
                { text: 'Cancel', action, data: false },
                { text: 'Confirm', action, data: true }
            ]
        });
    }

    public confirmInput(
        message: string,
        inputType: 'text' | 'number',
        action?: (agree: boolean, inputValue: any) => Promise<any>,
        inputValue?: string,
        parameters?: { [key: string]: string } | string,
        tag?: string,
        plural?: boolean,
    ) {
        this.show({
            title: 'Please Input',
            message,
            inputType,
            type: 'confirm',
            inputValue,
            actions: [
                { text: 'Cancel', action, data: false },
                { text: 'Confirm', action, data: true }
            ]
        });
    }

    public deletionConfirm(targetName: string, action: DialogAction<boolean>, description?: string, title?: string) {
        this.show({
            title: title || `Are you sure to delete ${targetName}?`,
            message: 'This operation is not reversible.',
            type: 'confirm',
            actions: [
                { text: 'Cancel', action, data: false },
                { text: 'Confirm', action, data: true }
            ]
        });
    }

    public onMessage(onMessage: (message: DialogMessage) => void) {
        this.receiver = onMessage;
        if (this.queue.length > 0) {
            for (const m of this.queue) {
                onMessage(m);
            }

            this.queue = [];
        }
    }

    public offMessage() {
        this.receiver = undefined;
    }
}

export const dialogControl = new DialogControl();
